import { Button, Text, Tooltip } from '@chakra-ui/react';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { DocumentStatus } from '@blockpulse3/graphql/hooks';

import { ResponsiveModalProps } from '../ResponsiveModal';
import { PreviewDocumentModal } from './PreviewDocumentModal';

export const CONTENT_LOADING = Symbol('Symbol For Loading Content');
export const CONTENT_ERROR = Symbol('Symbol For Error On Loading');

type Props = {
  /* ** User role (ie. redirection in same tab) ** */
  isUserSigner: boolean;
  /* ** `onSign()` loading status, ie. signature loading ** */
  isSignatureLoading: boolean;
  /* ** Is Submit button disabled ** */
  isDisabled?: boolean;
  /* ** Modal title ** */
  title: string;
  /* ** Modal subtitle ** */
  subTitle?: string;
  /* ** Source of the document to preview ** */
  src: string | symbol | null;
  /* ** Signing status of the document ** */
  status: DocumentStatus | null;
  /* ** Callback on sign button click ** */
  onSign?: () => void;
  /* ** Callback on cancel button click ** */
  onCancel?: () => void;
} & Omit<ResponsiveModalProps, 'children'>;

export function SignDocumentModal({
  isUserSigner,
  isSignatureLoading,
  isDisabled = false,
  status,
  onSign = noop,
  onCancel = noop,
  ...props
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nPreviewModal = useTranslations('PreviewModal');

  const signDocumentLabel = (): string => {
    if (isUserSigner) {
      return i18nPreviewModal(status ? 'UserSignerRemindLabel' : 'UserSignerLabel');
    }
    return i18nPreviewModal(status ? 'UserNotSignerRemindLabel' : 'UserNotSignerLabel');
  };

  const handleCancelClick = (): void => {
    onCancel();
  };

  const handleSignClick = (): void => {
    onSign();
  };
  const remindDisabled = isDisabled && !isUserSigner;
  return (
    <PreviewDocumentModal {...props}>
      <Button variant="secondary" onClick={handleCancelClick}>
        {i18nGeneral('Cancel')}
      </Button>
      {status !== DocumentStatus.SIGNED && status !== DocumentStatus.CANCELLED && (
        <Tooltip
          hasArrow
          isDisabled={!remindDisabled}
          label={<Text textAlign="center">{i18nPreviewModal('TooltipRemindDisabled')}</Text>}
          placement="top"
        >
          <Button
            data-cy="sign-document"
            isDisabled={isDisabled && !isUserSigner}
            isLoading={isSignatureLoading}
            loadingText={i18nPreviewModal('ButtonLoading')}
            onClick={handleSignClick}
          >
            {signDocumentLabel()}
          </Button>
        </Tooltip>
      )}
    </PreviewDocumentModal>
  );
}

export type SignDocumentModalProps = Props;
